import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { formatCurrency, API_URL } from "../lib/utils";
import { useBarcodeScanner, normalizeBarcode, beep } from "../lib/barcode";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Warehouse,
  AlertTriangle,
  Package,
  Plus,
  Minus,
  History,
  RefreshCw,
  ArrowUpCircle,
  ArrowDownCircle,
  ClipboardList,
  Filter,
} from "lucide-react";

const StockPage = () => {
  const { t } = useLanguage();
  const { token, isManager } = useAuth();
  const [products, setProducts] = useState([]);
  const [stockAlerts, setStockAlerts] = useState({ out_of_stock: [], low_stock: [], total_alerts: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("products");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStock, setFilterStock] = useState("all"); // all, low, out

  // Stock Movement Modal
  const [movementModal, setMovementModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movementType, setMovementType] = useState("in");
  const [movementForm, setMovementForm] = useState({
    quantity: "",
    reason: "compra",
    notes: "",
  });

  // History Modal
  const [historyModal, setHistoryModal] = useState(false);
  const [stockHistory, setStockHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Inventory Modal (bulk update)
  const [inventoryModal, setInventoryModal] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products/stock-alerts`, { headers, withCredentials: true }),
      ]);
      
      // Filtrar apenas produtos com controlo de stock ou mostrar todos
      const allProducts = productsRes.data;
      setProducts(allProducts);
      setStockAlerts(alertsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar produtos
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || (product.barcode && searchQuery && product.barcode.includes(normalizeBarcode(searchQuery)));
    
    if (filterStock === "low") {
      return matchesSearch && product.track_stock && product.current_stock <= product.min_stock && product.current_stock > 0;
    }
    if (filterStock === "out") {
      return matchesSearch && product.track_stock && product.current_stock <= 0;
    }
    if (filterStock === "tracked") {
      return matchesSearch && product.track_stock;
    }
    return matchesSearch;
  });

  // Abrir modal de movimento de stock
  const openMovementModal = (product, type = "in") => {
    setSelectedProduct(product);
    setMovementType(type);
    setMovementForm({ quantity: "", reason: type === "in" ? "compra" : "venda", notes: "" });
    setMovementModal(true);
  };

  // Leitor de código de barras: ler um artigo abre logo a entrada de stock desse artigo
  useBarcodeScanner((raw) => {
    const code = normalizeBarcode(raw);
    const product = products.find((p) => p.barcode === code);
    if (!product) { beep(false); toast.error(`Código ${code} não corresponde a nenhum artigo`); return; }
    beep(true);
    if (!product.track_stock) { toast.info(`${product.name} não tem controlo de stock activo`); return; }
    openMovementModal(product, "in");
  }, { enabled: !movementModal });

  // Submeter movimento de stock
  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !movementForm.quantity) {
      toast.error("Preencha a quantidade");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/products/${selectedProduct.product_id}/stock-movement`,
        {
          product_id: selectedProduct.product_id,
          quantity: parseInt(movementForm.quantity),
          movement_type: movementType,
          reason: movementForm.reason,
          notes: movementForm.notes,
        },
        { headers, withCredentials: true }
      );

      toast.success(
        movementType === "in"
          ? `+${movementForm.quantity} adicionado ao stock`
          : `-${movementForm.quantity} removido do stock`
      );
      setMovementModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar stock");
    }
  };

  // Ver histórico de um produto
  const viewHistory = async (product) => {
    setSelectedProduct(product);
    setHistoryLoading(true);
    setHistoryModal(true);

    try {
      const res = await axios.get(
        `${API_URL}/products/${product.product_id}/stock-history`,
        { headers, withCredentials: true }
      );
      setStockHistory(res.data);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
      setStockHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Abrir modal de inventário
  const openInventoryModal = () => {
    const trackedProducts = products.filter(p => p.track_stock);
    setInventoryData(
      trackedProducts.map(p => ({
        product_id: p.product_id,
        name: p.name,
        current_stock: p.current_stock || 0,
        new_stock: p.current_stock || 0,
        stock_unit: p.stock_unit || "unidade",
      }))
    );
    setInventoryModal(true);
  };

  // Submeter inventário
  const handleInventorySubmit = async () => {
    const updates = inventoryData
      .filter(p => p.new_stock !== p.current_stock)
      .map(p => ({
        product_id: p.product_id,
        current_stock: parseInt(p.new_stock),
      }));

    if (updates.length === 0) {
      toast.info("Nenhuma alteração para guardar");
      return;
    }

    try {
      await axios.post(`${API_URL}/products/bulk-stock-update`, updates, {
        headers,
        withCredentials: true,
      });
      toast.success(`${updates.length} produto(s) atualizado(s)`);
      setInventoryModal(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar inventário");
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("pt-AO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Razões de movimento
  const movementReasons = {
    in: [
      { value: "compra", label: "Compra" },
      { value: "devolução", label: "Devolução de Cliente" },
      { value: "ajuste", label: "Ajuste de Inventário" },
      { value: "transferência", label: "Transferência" },
      { value: "outro", label: "Outro" },
    ],
    out: [
      { value: "venda", label: "Venda" },
      { value: "perda", label: "Perda/Desperdício" },
      { value: "danificado", label: "Danificado" },
      { value: "ajuste", label: "Ajuste de Inventário" },
      { value: "outro", label: "Outro" },
    ],
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("stock")}</h1>
          <p className="text-muted-foreground">Gestão de stock dos produtos · leia um código de barras para dar entrada</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-stock-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={openInventoryModal} data-testid="inventory-btn">
            <ClipboardList className="w-4 h-4 mr-2" />
            Inventário
          </Button>
        </div>
      </div>

      {/* Alerts Banner */}
      {stockAlerts.total_alerts > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <div className="flex-1">
                <p className="font-semibold text-amber-700 dark:text-amber-400">
                  Alertas de Stock ({stockAlerts.total_alerts})
                </p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {stockAlerts.out_of_stock.length > 0 && (
                    <Badge variant="destructive">
                      {stockAlerts.out_of_stock.length} esgotado(s)
                    </Badge>
                  )}
                  {stockAlerts.low_stock.length > 0 && (
                    <Badge variant="outline" className="border-amber-500 text-amber-600">
                      {stockAlerts.low_stock.length} com stock baixo
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterStock("out")}
                className="text-amber-600 border-amber-500"
              >
                Ver Esgotados
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Pesquisar produto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="stock-search-input"
          />
        </div>
        <Select value={filterStock} onValueChange={setFilterStock}>
          <SelectTrigger className="w-[200px]" data-testid="stock-filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por stock" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Produtos</SelectItem>
            <SelectItem value="tracked">Com Controlo de Stock</SelectItem>
            <SelectItem value="low">Stock Baixo</SelectItem>
            <SelectItem value="out">Esgotados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Produtos ({filteredProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto</TableHead>
                <TableHead className="text-center">Stock Actual</TableHead>
                <TableHead className="text-center">Stock Mínimo</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Preço</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => {
                const hasStockControl = product.track_stock;
                const currentStock = product.current_stock || 0;
                const minStock = product.min_stock || 0;
                const isOutOfStock = hasStockControl && currentStock <= 0;
                const isLowStock = hasStockControl && currentStock > 0 && currentStock <= minStock;

                return (
                  <TableRow key={product.product_id} data-testid={`stock-row-${product.product_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Package className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{product.name}</p>
                          {!hasStockControl && (
                            <span className="text-xs text-muted-foreground">Sem controlo de stock</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {hasStockControl ? (
                        <span className={`font-mono font-semibold ${
                          isOutOfStock ? "text-red-500" : isLowStock ? "text-amber-500" : "text-emerald-500"
                        }`}>
                          {currentStock} {product.stock_unit || "un."}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {hasStockControl ? (
                        <span className="font-mono">{minStock} {product.stock_unit || "un."}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {!hasStockControl && (
                        <Badge variant="secondary">Sem Controlo</Badge>
                      )}
                      {isOutOfStock && (
                        <Badge variant="destructive">ESGOTADO</Badge>
                      )}
                      {isLowStock && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">
                          Stock Baixo
                        </Badge>
                      )}
                      {hasStockControl && !isOutOfStock && !isLowStock && (
                        <Badge variant="outline" className="border-emerald-500 text-emerald-600">
                          OK
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {formatCurrency(product.price)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {hasStockControl && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMovementModal(product, "in")}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`add-stock-${product.product_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMovementModal(product, "out")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-stock-${product.product_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewHistory(product)}
                              data-testid={`history-${product.product_id}`}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Warehouse className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum produto encontrado</p>
                    {filterStock !== "all" && (
                      <Button variant="link" onClick={() => setFilterStock("all")}>
                        Ver todos os produtos
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Stock Movement Modal */}
      <Dialog open={movementModal} onOpenChange={setMovementModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {movementType === "in" ? (
                <>
                  <ArrowUpCircle className="w-5 h-5 text-emerald-500" />
                  Entrada de Stock
                </>
              ) : (
                <>
                  <ArrowDownCircle className="w-5 h-5 text-red-500" />
                  Saída de Stock
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <form onSubmit={handleMovementSubmit} className="space-y-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{selectedProduct.name}</p>
                <p className="text-sm text-muted-foreground">
                  Stock atual: <span className="font-mono font-semibold">{selectedProduct.current_stock || 0} {selectedProduct.stock_unit || "un."}</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantidade *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                    placeholder="0"
                    required
                    data-testid="movement-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Razão *</Label>
                  <Select
                    value={movementForm.reason}
                    onValueChange={(value) => setMovementForm({ ...movementForm, reason: value })}
                  >
                    <SelectTrigger data-testid="movement-reason-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {movementReasons[movementType].map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={movementForm.notes}
                  onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                  placeholder="Ex: Factura #123, Fornecedor XYZ..."
                  rows={2}
                  data-testid="movement-notes-input"
                />
              </div>

              {movementType === "in" && movementForm.quantity && (
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200">
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    Novo stock: <span className="font-mono font-bold">
                      {(selectedProduct.current_stock || 0) + parseInt(movementForm.quantity || 0)} {selectedProduct.stock_unit || "un."}
                    </span>
                  </p>
                </div>
              )}

              {movementType === "out" && movementForm.quantity && (
                <div className={`p-3 rounded-lg border ${
                  parseInt(movementForm.quantity) > (selectedProduct.current_stock || 0)
                    ? "bg-red-50 dark:bg-red-950/20 border-red-200"
                    : "bg-amber-50 dark:bg-amber-950/20 border-amber-200"
                }`}>
                  <p className={`text-sm ${
                    parseInt(movementForm.quantity) > (selectedProduct.current_stock || 0)
                      ? "text-red-700 dark:text-red-400"
                      : "text-amber-700 dark:text-amber-400"
                  }`}>
                    {parseInt(movementForm.quantity) > (selectedProduct.current_stock || 0) ? (
                      "Stock insuficiente!"
                    ) : (
                      <>
                        Novo stock: <span className="font-mono font-bold">
                          {(selectedProduct.current_stock || 0) - parseInt(movementForm.quantity || 0)} {selectedProduct.stock_unit || "un."}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMovementModal(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className={movementType === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  disabled={!movementForm.quantity || (movementType === "out" && parseInt(movementForm.quantity) > (selectedProduct.current_stock || 0))}
                  data-testid="submit-movement-btn"
                >
                  {movementType === "in" ? "Adicionar Stock" : "Remover Stock"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyModal} onOpenChange={setHistoryModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Stock - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
          ) : stockHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum movimento registado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-center">Qtd</TableHead>
                  <TableHead>Razão</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Por</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockHistory.map((mov) => (
                  <TableRow key={mov.movement_id}>
                    <TableCell className="text-sm">{formatDate(mov.created_at)}</TableCell>
                    <TableCell>
                      {mov.movement_type === "in" ? (
                        <Badge className="bg-emerald-500">Entrada</Badge>
                      ) : (
                        <Badge className="bg-red-500">Saída</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-mono font-semibold">
                      {mov.movement_type === "in" ? "+" : "-"}{mov.quantity}
                    </TableCell>
                    <TableCell className="capitalize">{mov.reason}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {mov.stock_before} → {mov.stock_after}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {mov.created_by_name || "Sistema"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Inventory Modal */}
      <Dialog open={inventoryModal} onOpenChange={setInventoryModal}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Contagem de Inventário
            </DialogTitle>
          </DialogHeader>
          
          {inventoryData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum produto com controlo de stock activado</p>
              <p className="text-sm mt-1">Active o controlo de stock nos produtos primeiro</p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-center">Stock Sistema</TableHead>
                      <TableHead className="text-center">Stock Real</TableHead>
                      <TableHead className="text-center">Diferença</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryData.map((item, index) => {
                      const diff = item.new_stock - item.current_stock;
                      return (
                        <TableRow key={item.product_id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell className="text-center font-mono">
                            {item.current_stock} {item.stock_unit}
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="0"
                              value={item.new_stock}
                              onChange={(e) => {
                                const newData = [...inventoryData];
                                newData[index].new_stock = parseInt(e.target.value) || 0;
                                setInventoryData(newData);
                              }}
                              className="w-24 mx-auto text-center font-mono"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            {diff !== 0 && (
                              <span className={`font-mono font-semibold ${diff > 0 ? "text-emerald-500" : "text-red-500"}`}>
                                {diff > 0 ? "+" : ""}{diff}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter className="border-t pt-4">
                <div className="flex-1 text-sm text-muted-foreground">
                  {inventoryData.filter(p => p.new_stock !== p.current_stock).length} alteração(ões) pendente(s)
                </div>
                <Button type="button" variant="outline" onClick={() => setInventoryModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleInventorySubmit} data-testid="save-inventory-btn">
                  Guardar Inventário
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockPage;
